import { useState } from 'react';
import { SUPPORT_EMAIL } from '../constants/site';
import { submitLead } from '../utils/submitLead';
import { openSalesIQChat } from '../utils/openSalesIQChat';

export default function SupportSection() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const form = event.currentTarget;
    const data = new FormData(form);
    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const phone = String(data.get('phone') || '').trim();
    const message = String(data.get('message') || '').trim();

    if (!phone) {
      setError('Please enter your phone number.');
      return;
    }

    setPending(true);

    try {
      await submitLead({
        name,
        email,
        phone,
        message,
        source: 'support-form',
        _honey: String(data.get('_honey') || ''),
      });
      setSent(true);
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="bg-[#22c55e] text-white py-16 md:py-20" id="support">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Expert Support When You Need It</h2>
          <div className="w-24 h-px bg-white/30 mx-auto mb-8" />
          <p className="text-base md:text-lg max-w-2xl mx-auto leading-relaxed text-white/95">
            Need help with installation, migration, or learning the software? Send a request or chat
            with our team—we guide you every step of the way.
          </p>
        </div>

        <div className="max-w-xl mx-auto bg-white text-[#1a263d] rounded-xl shadow-lg p-6 md:p-8">
          {sent ? (
            <div className="text-center space-y-4">
              <h3 className="text-xl font-bold m-0">Request received</h3>
              <p className="text-[#4a4038] m-0">
                Thanks — we will follow up shortly. You can also email{' '}
                <a className="text-[#15803d] font-semibold" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
              <button
                type="button"
                className="bg-[#15803d] hover:bg-[#166534] text-white font-semibold py-2.5 px-6 rounded-lg"
                onClick={() => {
                  setSent(false);
                  setError('');
                }}
              >
                Send another request
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <input
                type="text"
                name="_honey"
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <h3 className="text-lg font-bold m-0">Support request</h3>
              <label className="block text-sm font-medium">
                Full name
                <input
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Your name"
                />
              </label>
              <label className="block text-sm font-medium">
                Email
                <input
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="you@email.com"
                />
              </label>
              <label className="block text-sm font-medium">
                Phone
                <input
                  name="phone"
                  type="tel"
                  required
                  autoComplete="tel"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="+1 555 000 0000"
                />
              </label>
              <label className="block text-sm font-medium">
                How can we help?
                <textarea
                  name="message"
                  required
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Install, upgrade, sync, TreeVault…"
                />
              </label>
              {error ? <p className="text-red-600 text-sm m-0">{error}</p> : null}
              <button
                type="submit"
                disabled={pending}
                className="w-full bg-[#15803d] hover:bg-[#166534] disabled:opacity-70 text-white font-semibold py-3 rounded-lg"
              >
                {pending ? 'Sending…' : 'Submit request'}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-8">
          <button
            type="button"
            onClick={openSalesIQChat}
            className="bg-[#1a263d] hover:bg-[#0f1729] text-white font-semibold py-3 px-10 rounded-lg text-base transition-colors shadow-md"
          >
            Chat Now
          </button>
        </div>
      </div>
    </section>
  );
}
